/**
 * Settles a team write the browser waits on (a page move, a typed page or type
 * placement) from what the TeamRoom sends back:
 *
 * - the echo of the row (the server broadcasts these writes to their author)
 *   that holds the wanted state confirms it;
 * - a server error naming the write's request id refuses it;
 * - a server error naming no request id makes every sent write a suspect, and
 *   the re-read list that follows refuses a suspect it does not hold;
 * - nothing within the timeout fails it.
 *
 * Without this a caller hears success the moment a write is sent, and Set type
 * would trash a page whose children the server just refused to move.
 */
export const WEB_WRITE_CONFIRM_TIMEOUT_MS = 6_000;

export type WriteKind = 'doc' | 'type' | 'item';

interface Pending {
  kind: WriteKind;
  id: string;
  /** `row` is null when the server holds no row for the id. */
  holds: (row: unknown) => boolean;
  requestId?: string;
  /** Written to an open socket, so a list re-read after an error reflects it. */
  sent: boolean;
  suspect: boolean;
  resolve: () => void;
  reject: (error: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

const ACTION: Record<WriteKind, string> = { doc: 'move', type: 'placement', item: 'placement' };

export class WebWriteConfirmations {
  private readonly pending = new Set<Pending>();

  constructor(private readonly timeoutMs = WEB_WRITE_CONFIRM_TIMEOUT_MS) {}

  /** Register before sending; resolves on confirmation, rejects on refusal or timeout. */
  expect<Row>(
    kind: WriteKind,
    id: string,
    holds: (row: Row | null) => boolean,
    options: { requestId?: string; sent: boolean },
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const entry: Pending = {
        kind,
        id,
        holds: holds as (row: unknown) => boolean,
        ...(options.requestId ? { requestId: options.requestId } : {}),
        sent: options.sent,
        suspect: false,
        resolve,
        reject,
        timer: setTimeout(() => this.settle(entry, new Error(`The server did not confirm the ${ACTION[kind]} in time.`)), this.timeoutMs),
      };
      this.pending.add(entry);
    });
  }

  /** The server's current row for one id, or null once it is gone. */
  changed(kind: WriteKind, id: string, row: unknown): void {
    for (const entry of this.pending) if (entry.kind === kind && entry.id === id && entry.holds(row)) this.settle(entry);
  }

  /** A server error: refuses the write it names, or makes every sent write a suspect. */
  error(requestId: string | undefined, message: string): void {
    for (const entry of this.pending) {
      if (requestId) {
        if (entry.requestId === requestId) this.settle(entry, new Error(`The server refused the ${ACTION[entry.kind]}: ${message}`));
      } else if (entry.sent) {
        entry.suspect = true;
      }
    }
  }

  /** A full list from the server: what it holds is confirmed, a suspect it lacks is refused. */
  loaded(kind: WriteKind, rows: ReadonlyMap<string, unknown>): void {
    for (const entry of this.pending) {
      if (entry.kind !== kind) continue;
      if (entry.holds(rows.get(entry.id) ?? null)) this.settle(entry);
      else if (entry.suspect) this.settle(entry, new Error(`The server refused the ${ACTION[kind]}.`));
    }
  }

  dispose(): void {
    for (const entry of this.pending) this.settle(entry, new Error('The team connection closed.'));
  }

  private settle(entry: Pending, error?: Error): void {
    if (!this.pending.delete(entry)) return;
    clearTimeout(entry.timer);
    if (error) entry.reject(error);
    else entry.resolve();
  }
}
